/**
 * Client-only stable id for materiality line items (Risk Assessment edit/delete/notes).
 * Must be stripped from every backend payload — never persist this key to the API.
 */
import { v4 as uuidv4 } from 'uuid';

export const CLIENT_LINE_ITEM_UUID_KEY = '__clientLineItemUuid';

function itemsAllHaveClientUuid(items) {
  if (!Array.isArray(items) || items.length === 0) return true;
  return items.every((row) => row && typeof row === 'object' && row[CLIENT_LINE_ITEM_UUID_KEY]);
}

/** @param {Record<string, unknown>|null|undefined} item */
export function ensureClientLineItemUuidOnItem(item) {
  if (!item || typeof item !== 'object') return item;
  if (item[CLIENT_LINE_ITEM_UUID_KEY]) return item;
  return { ...item, [CLIENT_LINE_ITEM_UUID_KEY]: uuidv4() };
}

/**
 * Assigns `CLIENT_LINE_ITEM_UUID_KEY` to each BS/IS row (immutable copy of `lineItems`).
 * Returns the same reference if every row already has a client UUID (avoids needless re-renders).
 * @param {Record<string, unknown> | null | undefined} lineItems
 */
export function ensureClientUuidsOnLineItemsObject(lineItems) {
  if (!lineItems || typeof lineItems !== 'object') return lineItems;
  const bsItems = lineItems.balance_sheet?.items;
  const isItems = getIncomeStatementItems(lineItems);
  if (itemsAllHaveClientUuid(bsItems) && itemsAllHaveClientUuid(isItems)) {
    return lineItems;
  }
  const next = { ...lineItems };
  if (Array.isArray(bsItems)) {
    next.balance_sheet = {
      ...next.balance_sheet,
      items: bsItems.map((row) => ensureClientLineItemUuidOnItem(row)),
    };
  }
  if (Array.isArray(isItems)) {
    next.income_statement = {
      ...next.income_statement,
      items: isItems.map((row) => ensureClientLineItemUuidOnItem(row)),
    };
  }
  return next;
}

/** @param {Record<string, unknown>} lineItems */
function getIncomeStatementItems(lineItems) {
  return lineItems.income_statement?.items;
}

/** @param {Record<string, unknown>|null|undefined} item */
export function stripClientLineItemUuidFromItem(item) {
  if (!item || typeof item !== 'object') return item;
  if (!Object.prototype.hasOwnProperty.call(item, CLIENT_LINE_ITEM_UUID_KEY)) return item;
  const { [CLIENT_LINE_ITEM_UUID_KEY]: _removed, ...rest } = item;
  return rest;
}

/**
 * Deep strip of `CLIENT_LINE_ITEM_UUID_KEY` (e.g. merged save payloads, line item clones).
 * @param {unknown} value
 */
export function stripClientLineItemUuidDeep(value) {
  if (value == null) return value;
  if (Array.isArray(value)) {
    return value.map((v) => stripClientLineItemUuidDeep(v));
  }
  if (typeof value === 'object') {
    const obj = value;
    const hasKey = Object.prototype.hasOwnProperty.call(obj, CLIENT_LINE_ITEM_UUID_KEY);
    const rest = { ...obj };
    if (hasKey) delete rest[CLIENT_LINE_ITEM_UUID_KEY];
    for (const k of Object.keys(rest)) {
      rest[k] = stripClientLineItemUuidDeep(rest[k]);
    }
    return rest;
  }
  return value;
}

/**
 * Copy legacy index-based note keys onto client-uuid keys after line items are stamped.
 * @param {Record<string, string>} prevNotes
 * @param {{ balance_sheet?: { items?: unknown[] }, income_statement?: { items?: unknown[] } } | null | undefined} lineItemsBundle
 * @param {number} docIdx
 */
export function migrateLineItemNotesToClientKeys(prevNotes, lineItemsBundle, docIdx) {
  if (!prevNotes || typeof prevNotes !== 'object' || !lineItemsBundle) return prevNotes;
  const next = { ...prevNotes };

  const bs = lineItemsBundle.balance_sheet?.items;
  if (Array.isArray(bs)) {
    bs.forEach((item, idx) => {
      const uid = item[CLIENT_LINE_ITEM_UUID_KEY];
      if (!uid) return;
      const legacy = `bs_${docIdx}_${idx}`;
      if (
        (!Object.prototype.hasOwnProperty.call(next, uid) || next[uid] === '') &&
        Object.prototype.hasOwnProperty.call(next, legacy)
      ) {
        next[uid] = next[legacy];
      }
    });
  }

  const is = getIncomeStatementItems(lineItemsBundle);
  if (Array.isArray(is)) {
    is.forEach((item, idx) => {
      const uid = item[CLIENT_LINE_ITEM_UUID_KEY];
      if (!uid) return;
      const stableKey = `is_${docIdx}_${idx}`;
      const itemType = item.category === 'revenue' ? 'is_revenue' : 'is_expense';
      const typedKey = `${itemType}_${docIdx}_${idx}`;
      const legKey = `income_statement_${docIdx}_${idx}`;
      const legacyVal =
        (Object.prototype.hasOwnProperty.call(next, stableKey) ? next[stableKey] : undefined) ??
        (Object.prototype.hasOwnProperty.call(next, typedKey) ? next[typedKey] : undefined) ??
        (Object.prototype.hasOwnProperty.call(next, legKey) ? next[legKey] : undefined);
      if (
        legacyVal !== undefined &&
        legacyVal !== '' &&
        (!Object.prototype.hasOwnProperty.call(next, uid) || next[uid] === '')
      ) {
        next[uid] = legacyVal;
      }
    });
  }

  return next;
}

/** First non-empty string value for an own property among `keys` (in order). */
export function pickFirstNonEmptyLineItemNote(notes, keys) {
  if (!notes || typeof notes !== 'object') return '';
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (k == null || k === '') continue;
    if (!Object.prototype.hasOwnProperty.call(notes, k)) continue;
    const v = notes[k];
    if (v != null && v !== '') return String(v);
  }
  return '';
}

/**
 * Persisted `lineItemNotes` must not use client UUIDs as keys. Map back to legacy keys only.
 * @param {Record<string, string>} notesByClientKey
 * @param {{ balance_sheet?: { items?: unknown[] }, income_statement?: { items?: unknown[] } } | null | undefined} lineItemData
 * @param {number} docIdx
 */
export function serializeLineItemNotesForApi(notesByClientKey, lineItemData, docIdx) {
  const out = {};
  if (!notesByClientKey || typeof notesByClientKey !== 'object' || !lineItemData) return out;

  const bs = lineItemData.balance_sheet?.items;
  if (Array.isArray(bs)) {
    bs.forEach((item, idx) => {
      const uid = item[CLIENT_LINE_ITEM_UUID_KEY];
      const legacy = `bs_${docIdx}_${idx}`;
      const val = pickFirstNonEmptyLineItemNote(notesByClientKey, [uid, legacy]);
      if (val !== '') out[legacy] = val;
    });
  }

  const is = getIncomeStatementItems(lineItemData);
  if (Array.isArray(is)) {
    is.forEach((item, idx) => {
      const uid = item[CLIENT_LINE_ITEM_UUID_KEY];
      const stableKey = `is_${docIdx}_${idx}`;
      const itemType = item.category === 'revenue' ? 'is_revenue' : 'is_expense';
      const typedKey = `${itemType}_${docIdx}_${idx}`;
      const legKey = `income_statement_${docIdx}_${idx}`;
      const val = pickFirstNonEmptyLineItemNote(notesByClientKey, [uid, stableKey, typedKey, legKey]);
      if (val !== '') out[stableKey] = val;
    });
  }

  return out;
}
